import React from "react";
import { Card, H1, H3, Button, ControlGroup, Tag, HTMLSelect, Spinner, NonIdealState, Text } from "@blueprintjs/core";
import { DateInput } from "@blueprintjs/datetime";
import { inject, observer } from "mobx-react";
import { withRouter } from "react-router-dom";
import { apiCallPost } from "../../../micro/fns/util";
import cogoToast from "cogo-toast";
import _ from "lodash";
import moment from "moment";
import SingleSelectWrapper from "../../../micro/utils/SingleSelectWrapper";
import { filterBookingsForSlotMetrics, shouldIncludeBookingInSlotMetrics } from "../../utils/surgerySlotUtils";

class DoctorwiseCalendar extends React.Component {
  state = {
    // Meta
    isLoadingMeta: false,
    isLoadingData: false,
    doctors: [],
    selectedDoctor: null,
    timezone: "",
    branches: [],

    // Date range
    dateFrom: new Date(),
    dateTo: moment().add(30, "days").toDate(),

    // Computed rows for table
    rows: [],

    // Hours from 6 AM to 8 PM inclusive
    hours: Array.from({ length: 15 }, (_, i) => i + 6),
  };

  componentDidMount() {
    // Detect timezone for display
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
      this.setState({ timezone: tz });
    } catch (e) {}
    this.loadDoctors();
    this.loadBranches();
  }

  getDoctorDisplayName = (doctor) => {
    if (!doctor) return "";
    const name = String(doctor.name || doctor.doctorName || "").trim();
    const shortcode = String(doctor.shortcode || doctor.shortCode || doctor.code || "").trim();
    if (shortcode && name) return `${shortcode} | ${name}`;
    if (name) return name;
    return shortcode;
  };

  createDoctorItems = () => {
    const doctorItems = this.state.doctors
      .map((doctor) => this.getDoctorDisplayName(doctor))
      .filter((label) => label && label.length > 0);
    return ["Select Doctor", ...doctorItems];
  };

  normalizeBranchName = (name) => {
    if (!name || typeof name === "object") return "";
    return String(name).trim().toLowerCase().replace(/\s+/g, " ");
  };

  normalizeBranchTokenValue = (value) => {
    if (value === null || value === undefined || typeof value === "object") return "";
    return this.normalizeBranchName(value);
  };

  getBranchTokensFromBooking = (booking = {}) => {
    const rawTokens = [
      booking.branchId,
      booking.branchCode,
      booking.branch,
      booking.branchName,
      booking.site,
      booking.siteId,
      booking.siteCode,
    ];
    return Array.from(
      new Set(
        rawTokens
          .map((token) => this.normalizeBranchTokenValue(token))
          .filter((token) => token && token.length > 0)
      )
    );
  };

  getBranchTokensFromSchedule = (schedule = {}, fallbackLabel = "") => {
    const rawTokens = [
      schedule.branchId,
      schedule.branchCode,
      schedule.branch,
      schedule.branchName,
      schedule.site,
      schedule.siteId,
      schedule.siteCode,
      fallbackLabel,
    ];
    return Array.from(
      new Set(
        rawTokens
          .map((token) => this.normalizeBranchTokenValue(token))
          .filter((token) => token && token.length > 0)
      )
    );
  };

  isBookingInBranch = (booking, scheduleSample, branchLabel) => {
    const bookingTokens = this.getBranchTokensFromBooking(booking);
    const scheduleTokens = this.getBranchTokensFromSchedule(scheduleSample, branchLabel);
    if (!bookingTokens.length || !scheduleTokens.length) return false;
    return bookingTokens.some((token) => scheduleTokens.includes(token));
  };

  parseTimeString = (value) => {
    if (!value || typeof value !== "string") return null;
    const cleaned = value.trim();
    if (!cleaned) return null;
    const formats = ["h:mm A", "hh:mm A", "h:mmA", "hh:mmA", "H:mm", "HH:mm"];
    for (let i = 0; i < formats.length; i += 1) {
      const parsed = moment(cleaned, formats[i], true);
      if (parsed.isValid()) return parsed;
    }
    const fallback = moment(cleaned);
    return fallback.isValid() ? fallback : null;
  };

  getTimeFrameKeyFromMoments = (startMoment, endMoment) => {
    if (!startMoment || !endMoment) return null;
    return `${startMoment.format("HH:mm")}-${endMoment.format("HH:mm")}`;
  };

  getTimeFrameKey = (timeFrameLabel) => {
    if (!timeFrameLabel || typeof timeFrameLabel !== "string") return null;
    const parts = timeFrameLabel.split("-");
    if (parts.length < 2) return null;
    const startMoment = this.parseTimeString(parts[0]);
    const endMoment = this.parseTimeString(parts.slice(1).join("-"));
    if (!startMoment || !endMoment) return null;
    return this.getTimeFrameKeyFromMoments(startMoment, endMoment);
  };

  buildSlotMeta = (schedule) => {
    if (!schedule) return null;
    let startRaw = schedule.timeFrom;
    let endRaw = schedule.timeTo;
    if ((!startRaw || !endRaw) && schedule.timeFrame) {
      const tfParts = schedule.timeFrame.split("-");
      if (tfParts.length >= 2) {
        if (!startRaw) startRaw = tfParts[0]?.trim();
        if (!endRaw) endRaw = tfParts.slice(1).join("-").trim();
      }
    }
    if (!startRaw || !endRaw) {
      if (schedule.timeFrame) {
        const parts = schedule.timeFrame.split("-");
        if (parts.length >= 2) {
          startRaw = startRaw || parts[0]?.trim();
          endRaw = endRaw || parts.slice(1).join("-").trim();
        }
      }
    }
    const startMoment = this.parseTimeString(startRaw);
    const endMoment = this.parseTimeString(endRaw);
    if (!startMoment || !endMoment) return null;
    const slotKey = this.getTimeFrameKeyFromMoments(startMoment, endMoment);
    const displayLabel = schedule.timeFrame
      ? schedule.timeFrame.trim()
      : `${startMoment.format("hh:mm A")} - ${endMoment.format("hh:mm A")}`;
    return {
      slotKey,
      display: displayLabel,
      startMoment,
      endMoment,
      startHour: parseInt(startMoment.format("H"), 10),
      endHour: parseInt(endMoment.format("H"), 10),
      timeFrom: startMoment.format("hh:mm A"),
      timeTo: endMoment.format("hh:mm A"),
    };
  };

  isMomentWithinSlot = (timeMoment, startMoment, endMoment) => {
    if (!timeMoment || !startMoment || !endMoment) return false;
    return timeMoment.isSameOrAfter(startMoment) && timeMoment.isBefore(endMoment);
  };

  computeSlotCountsForBranch = (bookings = [], slotMetaList = []) => {
    if (!bookings.length || !slotMetaList.length) return {};
    const counts = {};
    const slotByKey = {};
    slotMetaList.forEach((meta) => {
      slotByKey[meta.slotKey] = meta;
    });

    bookings.forEach((booking) => {
      const exactKey = this.getTimeFrameKey(booking.timeFrame);
      if (exactKey && slotByKey[exactKey]) {
        counts[exactKey] = (counts[exactKey] || 0) + 1;
        return;
      }

      const scheduledMoment = this.parseTimeString(booking.scheduledTime);
      if (scheduledMoment) {
        const fallbackSlot = slotMetaList.find((meta) =>
          this.isMomentWithinSlot(scheduledMoment, meta.startMoment, meta.endMoment)
        );
        if (fallbackSlot) {
          counts[fallbackSlot.slotKey] = (counts[fallbackSlot.slotKey] || 0) + 1;
          return;
        }
      }

      counts.__UNKNOWN__ = (counts.__UNKNOWN__ || 0) + 1;
    });

    return counts;
  };

  // Handle doctor selection from SingleSelectWrapper
  handleDoctorSelect = (selectedDoctorName) => {
    if (selectedDoctorName === "Select Doctor") {
      this.setState({ selectedDoctor: null, schedules: [], loadedSchedules: [] });
      return;
    }
    const doctor = this.state.doctors.find(
      (d) => this.getDoctorDisplayName(d) === selectedDoctorName || d.name === selectedDoctorName
    );
    if (doctor) {
      this.setState({ selectedDoctor: doctor }, () => {
        this.loadData();
      });
    }
  };

  // Load all consultants for the selector
  loadDoctors = async () => {
    try {
      this.setState({ isLoadingMeta: true });
      const response = await apiCallPost(`/rai/${this.props.authStore.prefix}/getAllConsultants`, {});
      const doctors = (response || []).map((d, idx) => {
        if (typeof d === "string") {
          return { _id: `doc_${idx}`, name: d };
        }
        return {
          _id: d._id || `doc_${idx}`,
          name: d.name || d.doctorName || "",
          shortcode: d.shortcode || d.shortCode || d.code || "",
        };
      });
      this.setState({ doctors, isLoadingMeta: false });
    } catch (err) {
      console.error("Failed to load doctors", err);
      cogoToast.error("Failed to load doctors");
      this.setState({ isLoadingMeta: false });
    }
  };

  // Load branches list (names)
  loadBranches = async () => {
    try {
      this.setState({ isLoadingMeta: true });
      const response = await apiCallPost(`/rai/${this.props.authStore.prefix}/getAllSites`, {});
      const branches = (response || []).map((name, idx) => ({ _id: `branch_${idx}`, name }));
      this.setState({ branches, isLoadingMeta: false });
    } catch (err) {
      console.error("Failed to load branches", err);
      cogoToast.error("Failed to load branches");
      this.setState({ isLoadingMeta: false });
    }
  };

  // Handle doctor selection
  // handleDoctorChange = async (e) => {
  //   const doctorId = e.target.value;
  //   const doctor = this.state.doctors.find((d) => d._id === doctorId) || null;
  //   this.setState({ selectedDoctor: doctor }, () => {
  //     if (doctor) this.loadData();
  //   });
  // };

  // Handle date changes
  handleDateFromChange = (d) => {
    const date = d || new Date();
    this.setState({ dateFrom: date }, () => {
      if (this.state.selectedDoctor) this.loadData();
    });
  };

  handleDateToChange = (d) => {
    const date = d || new Date();
    this.setState({ dateTo: date }, () => {
      if (this.state.selectedDoctor) this.loadData();
    });
  };

  // Main loader: schedules + bookings, then compute rows
  loadData = async () => {
    if (!this.state.selectedDoctor) return;

    try {
      this.setState({ isLoadingData: true });
      cogoToast.loading("Loading doctor schedule...");

      const { selectedDoctor, dateFrom, dateTo } = this.state;
      const fromStr = moment(dateFrom).format("YYYY-MM-DD");
      const toStr = moment(dateTo).format("YYYY-MM-DD");

      console.log("🔍 Loading data for doctor:", selectedDoctor.name, "from", fromStr, "to", toStr);

      // Fetch all schedules for this doctor (without branch filtering first)
      const schedulesResponse = await apiCallPost(
        `/v3/${this.props.authStore.prefix}/neoSxDoctorScheduleMaster/getAll`,
        {
          doctorId: selectedDoctor._id,
          effectiveFrom: fromStr,
          effectiveTo: toStr,
        }
      );

      console.log("🔍 Raw schedules response:", schedulesResponse);

      const schedules = Array.isArray(schedulesResponse) ? schedulesResponse : schedulesResponse?.data || [];
      console.log("🔍 Processed schedules:", schedules.length, schedules);

      // Fetch bookings (non-blocking; if fails, proceed with empty bookings)
      let bookings = [];
      try {
        const bookingsResponse = await apiCallPost(
          `/v3/${this.props.authStore.prefix}/neoSxBookingMaster/getSurgeries`,
          {
            doctorId: selectedDoctor._id,
            startDate: fromStr,
            endDate: toStr,
            page: 1,
            limit: 1000,
            mode: "calendar",
          }
        );
        const rawBookings = Array.isArray(bookingsResponse)
          ? bookingsResponse
          : bookingsResponse?.data?.items || bookingsResponse?.data || bookingsResponse?.items || [];
        bookings = filterBookingsForSlotMetrics(rawBookings);
      } catch (e) {
        console.warn("⚠️ Bookings fetch failed, continuing without counts", e);
        bookings = [];
      }

      console.log("🔍 Bookings:", bookings.length, bookings);

      // Group bookings by date for downstream branch-level filtering
      const bookingsByDate = _.groupBy(
        bookings.filter((bk) => bk?.scheduledDate && moment(bk.scheduledDate).isValid()),
        (bk) => moment(bk.scheduledDate).format("YYYY-MM-DD")
      );

      // Build date list (only next 30 days for performance)
      const dates = [];
      let cursor = moment(dateFrom).startOf("day");
      const end = moment(dateTo).startOf("day");
      while (cursor.isSameOrBefore(end)) {
        dates.push(cursor.clone());
        cursor.add(1, "day");
      }

      console.log(`🔍 Processing ${dates.length} dates from ${fromStr} to ${toStr}`);

      // For each date, compute branch-wise rows - ONLY for dates with schedules
      const rows = [];

      // Helper: robust day match (supports Mon/mon/Monday/Comma-separated)
      const dayMatches = (scheduleDay, dateMoment) => {
        if (scheduleDay === null || typeof scheduleDay === "undefined") return false;
        const targetIdx = parseInt(dateMoment.format("d"), 10); // 0=Sunday ... 6=Saturday
        const targetFull = dateMoment.format("dddd").toLowerCase();
        const targetAbbr = dateMoment.format("ddd").toLowerCase();

        // If numeric day provided
        if (typeof scheduleDay === "number") {
          return scheduleDay === targetIdx;
        }
        // If array provided
        if (Array.isArray(scheduleDay)) {
          return scheduleDay.some((d) => dayMatches(d, dateMoment));
        }
        // If string: split tokens and compare to names or numeric
        const tokens = String(scheduleDay)
          .split(/[\s,|/]+/)
          .map((t) => t.trim().toLowerCase())
          .filter(Boolean);
        return tokens.some((t) => {
          if (!isNaN(parseInt(t, 10))) {
            return parseInt(t, 10) === targetIdx;
          }
          return t === targetFull || t === targetAbbr;
        });
      };

      dates.forEach((d) => {
        const dateISO = d.format("YYYY-MM-DD");
        const dayName = d.format("dddd");
        const bookingsForDate = bookingsByDate[dateISO] || [];

        // Find schedules applicable for this date (day + within effective range)
        const dateSchedules = schedules.filter((s) => {
          if (!s) return false;

          // Check day match first
          const dayMatch = dayMatches(s.day, d);
          if (!dayMatch) return false;

          // Check effective date range
          const effFrom = s.effectiveFrom ? moment(s.effectiveFrom) : null;
          const effTo = s.effectiveTo ? moment(s.effectiveTo) : null;
          const inRange = (!effFrom || effFrom.isSameOrBefore(d, "day")) && (!effTo || effTo.isSameOrAfter(d, "day"));

          return inRange;
        });

        console.log(`🔍 Date ${dateISO} (${dayName}): ${dateSchedules.length} schedules found`);
        if (dateSchedules.length > 0) {
          console.log(
            "   Schedules:",
            dateSchedules.map(
              (s) => `${s.branchName} ${s.timeFrame || `${s.timeFrom} - ${s.timeTo}`} (${s.surgeryCount})`
            )
          );
        }

        // Skip dates with no schedules - this is the key requirement
        if (dateSchedules.length === 0) return;

        // Group by branch within the date - only branches that have schedules
        const dateByBranch = _.groupBy(dateSchedules, (s) => s.branchName || s.branch || s.site || "Unknown Branch");

        Object.keys(dateByBranch).forEach((branchName) => {
          const branchSchedulesRaw = dateByBranch[branchName] || [];
          if (branchSchedulesRaw.length === 0) return;

          const bookingsForBranch = bookingsForDate.filter((bk) =>
            this.isBookingInBranch(bk, branchSchedulesRaw[0], branchName)
          );

          const enrichedSchedules = branchSchedulesRaw
            .map((schedule) => {
              const slotMeta = this.buildSlotMeta(schedule);
              if (!slotMeta) {
                console.log("⚠️ Schedule missing usable time information:", schedule);
                return null;
              }
              return { schedule, slotMeta };
            })
            .filter(Boolean);

          if (!enrichedSchedules.length) {
            console.log(`⚠️ No valid slots for branch ${branchName} on ${dateISO}`);
            return;
          }

          console.log(`🔍 Branch ${branchName} on ${dateISO}: ${enrichedSchedules.length} schedules`);

          // Get surgery groups and compute 4-letter abbreviation
          const groups = _.uniq(
            branchSchedulesRaw
              .map((s) => s.surgeryGroups || s.surgeryGroup || s.group || s.surgeryType || s.type)
              .filter((g) => g && (Array.isArray(g) ? g.length > 0 : typeof g === "string" && g.trim() !== ""))
              .flat()
          );
          const abbr = groups
            .map((g) => String(g).trim())
            .map((g) => (g.length <= 4 ? g : g.replace(/[^A-Za-z]/g, "").slice(0, 4)))
            .join("/");

          const slotMetaList = enrichedSchedules.map((entry) => entry.slotMeta);
          const slotCounts = this.computeSlotCountsForBranch(bookingsForBranch, slotMetaList);
          const unknownCount = slotCounts.__UNKNOWN__ || 0;
          if (slotCounts.__UNKNOWN__) {
            console.log(
              `⚠️ ${slotCounts.__UNKNOWN__} surgeries on ${dateISO} at ${branchName} could not be matched to a slot`
            );
            delete slotCounts.__UNKNOWN__;
          }

          // Build hour map: hour -> array of slot info (allow multiple per hour)
          const hourMap = {};
          this.state.hours.forEach((h) => (hourMap[h] = []));

          enrichedSchedules.forEach(({ schedule: s, slotMeta }) => {
            const { display, timeFrom, timeTo, startHour, endHour, slotKey } = slotMeta;

            if (startHour < 6 || startHour > 20) {
              console.log("⚠️ Start hour outside range 6-20:", startHour);
              return;
            }

            const cappedEndHour = Math.min(endHour, 20);
            const booked = slotCounts[slotKey] || 0;
            const capacity = s.surgeryCount || 0;

            console.log(`🔍 Slot ${display} -> Hours ${startHour}-${endHour}: ${booked}/${capacity}`);

            // Add slot to all hours it spans (capped to display window)
            for (let hour = startHour; hour <= cappedEndHour; hour += 1) {
              hourMap[hour].push({
                timeFrame: display,
                timeFrom,
                timeTo,
                bookedCount: booked,
                capacity,
                isFullyBooked: capacity > 0 && booked >= capacity,
                isAvailable: capacity > 0,
                schedule: s,
                surgeryGroups: s.surgeryGroups || [],
                startHour,
                endHour,
              });
            }
          });

          // Add a row for the branch if there are schedules (even if timeFrames didn't fit)
          rows.push({
            key: `${dateISO}__${branchName}__${rows.length}`,
            dateISO,
            dateLabel: d.format("ddd, DD MMM"),
            branchName,
            surgeryGroup: groups.join(", "),
            surgeryGroupAbbr: abbr,
            hourMap,
            unknownCount,
          });
        });
      });

      console.log("🔍 Final rows:", rows.length, rows);

      // Sort rows by date asc, then branch name
      const sortedRows = _.sortBy(rows, ["dateISO", "branchName"]);

      this.setState({ rows: sortedRows, isLoadingData: false });
      cogoToast.success(`Schedule loaded: ${sortedRows.length} rows found`);
    } catch (err) {
      console.error("Failed to load calendar data", err);
      cogoToast.error("Failed to load calendar data");
      this.setState({ isLoadingData: false });
    }
  };

  // Navigate to create new surgery with prefilled params
  handleSlotClick = (dateISO, branchName, timeFrame) => {
    if (!this.state.selectedDoctor) return;
    const params = new URLSearchParams({
      branchId: branchName,
      branchName: branchName,
      doctorId: this.state.selectedDoctor._id,
      doctorName: this.state.selectedDoctor.name,
      date: dateISO,
      timeFrame: timeFrame,
      mode: "create",
    });
    this.props.history.push(`/v3/workflow/surgery-booking?${params.toString()}`);
  };

  // Download CSV exporter
  downloadCsv = () => {
    const { rows, hours } = this.state;
    const headers = [
      "Date",
      "Branch",
      ...hours.map((h) => moment().hour(h).minute(0).format("h A").replace(":00", "")),
    ];
    const lines = [headers.join(",")];

    rows.forEach((row) => {
      const cells = [row.dateLabel, row.branchName];
      hours.forEach((h) => {
        const slots = row.hourMap[h] || [];
        const totalCapacity = slots.reduce((sum, s) => sum + (s.capacity || 0), 0);
        const totalBooked = slots.reduce((sum, s) => sum + (s.bookedCount || 0), 0);
        const schedulesCount = slots.length;
        cells.push(`${totalBooked}/${totalCapacity}${schedulesCount ? ` x${schedulesCount}` : ""}`);
      });
      lines.push(cells.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","));
    });

    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "doctorwise-calendar.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  // Print preview with header summary
  openPrintPreview = () => {
    const { rows, hours, selectedDoctor, dateFrom, dateTo } = this.state;
    const win = window.open("", "_blank");
    if (!win) return;

    const style = `
      body { font-family: Arial, sans-serif; color: #1f2937; }
      .header { display:flex; justify-content:space-between; align-items:flex-start; margin-bottom: 12px; }
      .title { font-size: 20px; font-weight: 800; }
      .meta { text-align: right; font-size: 12px; color: #374151; }
      table { width: 100%; border-collapse: collapse; }
      th, td { border: 1px solid #e5e7eb; padding: 6px 8px; font-size: 12px; }
      th { background: #f3f4f6; }
    `;

    const headers = [
      "Date",
      "Branch",
      ...hours.map((h) => moment().hour(h).minute(0).format("h A").replace(":00", "")),
    ];
    const headHtml = `<tr>${headers.map((h) => `<th>${h}</th>`).join("")}</tr>`;

    const bodyHtml = rows
      .map((row) => {
        const cells = [row.dateLabel, row.branchName];
        hours.forEach((h) => {
          const slots = row.hourMap[h] || [];
          const totalCapacity = slots.reduce((sum, s) => sum + (s.capacity || 0), 0);
          const totalBooked = slots.reduce((sum, s) => sum + (s.bookedCount || 0), 0);
          const schedulesCount = slots.length;
          cells.push(`${totalBooked}/${totalCapacity}${schedulesCount ? ` x${schedulesCount}` : ""}`);
        });
        return `<tr>${cells.map((c) => `<td>${c}</td>`).join("")}</tr>`;
      })
      .join("");

    const nowStr = moment().format("DD/MM/YYYY HH:mm");
    const fromStr = moment(dateFrom).format("DD/MM/YYYY");
    const toStr = moment(dateTo).format("DD/MM/YYYY");
    const doctorName = selectedDoctor?.name || "-";

    win.document.write(`
      <html>
        <head>
          <title>Doctorwise Calendar</title>
          <style>${style}</style>
        </head>
        <body>
          <div class="header">
            <div class="title">${this.props.authStore?.hospitalName || "Disha Eye Hospital"}</div>
            <div class="meta">Generated at ${nowStr}</div>
          </div>
          <div style="margin-bottom: 12px; font-size: 13px;">
            <strong>Doctor Name:</strong> ${doctorName} &nbsp; &nbsp; <strong>Range:</strong> ${fromStr} → ${toStr}
          </div>
          <table>
            <thead>${headHtml}</thead>
            <tbody>${bodyHtml}</tbody>
          </table>
          <script>window.onload = function(){ window.print(); }</script>
        </body>
      </html>
    `);
    win.document.close();
  };

  renderHeader() {
    const { selectedDoctor } = this.state;
    return (
      <div style={{ marginBottom: "20px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
          <div>
            <H1 style={{ color: "var(--text-primary)", margin: 0 }}>Doctorwise Surgery Calendar</H1>
            <p style={{ color: "var(--text-secondary)", margin: "5px 0" }}>
              Select a doctor and a date range to view slot availability across branches. (Default: next 30 days)
            </p>
          </div>
        </div>
      </div>
    );
  }

  renderFilters() {
    const { doctors, selectedDoctor, dateFrom, dateTo, isLoadingMeta, timezone } = this.state;
    return (
      <Card className="v3-card" style={{ marginBottom: "20px" }}>
        <div className="v3-card-header">
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
            <H3 className="v3-card-title" style={{ margin: 0 }}>
              Filters
            </H3>
            <div style={{ display: "flex", gap: 8 }}>
              <Button small icon="download" onClick={this.downloadCsv}>
                Download
              </Button>
              <Button small icon="print" onClick={this.openPrintPreview}>
                Print
              </Button>
            </div>
          </div>
        </div>
        <div className="v3-card-body" style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
          <ControlGroup>
            <Tag
              large
              minimal={false}
              style={{
                color: "var(--text-primary)",
                backgroundColor: "var(--bg-surface)",
                border: "1px solid var(--border-color)",
                fontWeight: 600,
              }}
            >
              Doctor
            </Tag>

            <SingleSelectWrapper
              items={this.createDoctorItems()}
              selectedItem={this.getDoctorDisplayName(selectedDoctor) || null}
              placeholder="Search and select doctor..."
              onChange={this.handleDoctorSelect}
              width="250px"
              noResultsText="No doctors found"
              disabled={isLoadingMeta}
              inputProps={{
                large: true,
                leftIcon: "search",
              }}
            />
          </ControlGroup>

          <ControlGroup>
            <Tag
              large
              minimal={false}
              style={{
                color: "var(--text-primary)",
                backgroundColor: "var(--bg-surface)",
                border: "1px solid var(--border-color)",
                fontWeight: 600,
              }}
            >
              From
            </Tag>
            <DateInput
              formatDate={(d) => moment(d).format("DD/MM/YYYY")}
              parseDate={(str) => moment(str, "DD/MM/YYYY").toDate()}
              value={dateFrom}
              onChange={this.handleDateFromChange}
              popoverProps={{ usePortal: false }}
            />
          </ControlGroup>

          <ControlGroup>
            <Tag
              large
              minimal={false}
              style={{
                color: "var(--text-primary)",
                backgroundColor: "var(--bg-surface)",
                border: "1px solid var(--border-color)",
                fontWeight: 600,
              }}
            >
              To
            </Tag>
            <DateInput
              formatDate={(d) => moment(d).format("DD/MM/YYYY")}
              parseDate={(str) => moment(str, "DD/MM/YYYY").toDate()}
              value={dateTo}
              onChange={this.handleDateToChange}
              popoverProps={{ usePortal: false }}
            />
          </ControlGroup>

          <Button icon="refresh" onClick={() => this.loadData()} disabled={!selectedDoctor}>
            Refresh
          </Button>
        </div>
        {/* Summary just below filters */}
        {selectedDoctor && (
          <div
            style={{
              padding: "12px 16px",
              borderTop: "1px solid var(--border-color)",
              display: "flex",
              gap: 24,
              flexWrap: "wrap",
            }}
          >
            <Text style={{ color: "var(--text-secondary)" }}>
              <strong>Doctor:</strong> {selectedDoctor.name}
            </Text>
            <Text style={{ color: "var(--text-secondary)" }}>
              <strong>Range:</strong> {moment(dateFrom).format("DD/MM/YYYY")} → {moment(dateTo).format("DD/MM/YYYY")}
            </Text>
            <Text style={{ color: "var(--text-secondary)" }}>
              <strong>Time Zone:</strong> {timezone || "System Default"}
            </Text>
          </div>
        )}
      </Card>
    );
  }

  renderTable() {
    const { rows, hours, isLoadingData, selectedDoctor } = this.state;
    const hasUnknownColumn = rows.some((row) => (row.unknownCount || 0) > 0);

    // Compact column widths for tight grouping
    const DATE_COL_W = 88; // 20% smaller from 110
    const BRANCH_COL_W = 96; // 20% smaller from 120
    const HOUR_COL_W = 60; // 50% of prior width for denser hour columns

    const LEFT_DATE = 0;
    const LEFT_BRANCH = LEFT_DATE + DATE_COL_W;

    if (!selectedDoctor) {
      return (
        <Card className="v3-card" style={{ padding: 16, textAlign: "center" }}>
          <Text className="v3-text-secondary">Select a doctor and date range to view schedule</Text>
        </Card>
      );
    }

    if (isLoadingData) {
      return (
        <NonIdealState icon={<Spinner />} title="Loading schedule..." description="Fetching schedules and bookings" />
      );
    }

    return (
      <Card className="v3-card" style={{ padding: 0 }}>
        <div style={{ overflowX: "auto" }}>
          <table
            style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, color: "var(--text-primary)" }}
            className="bp3-html-table"
          >
            <thead>
              <tr>
                <th
                  style={{
                    position: "sticky",
                    left: LEFT_DATE,
                    background: "var(--bg-surface)",
                    zIndex: 3,
                    width: DATE_COL_W,
                    minWidth: DATE_COL_W,
                    maxWidth: DATE_COL_W,
                    color: "var(--text-primary)",
                  }}
                >
                  Date
                </th>
                <th
                  style={{
                    position: "sticky",
                    left: LEFT_BRANCH,
                    background: "var(--bg-surface)",
                    zIndex: 3,
                    width: BRANCH_COL_W,
                    minWidth: BRANCH_COL_W,
                    maxWidth: BRANCH_COL_W,
                    color: "var(--text-primary)",
                  }}
                >
                  Branch
                </th>
                {hours.map((h) => (
                  <th
                    key={h}
                    style={{
                      textAlign: "center",
                      width: HOUR_COL_W,
                      minWidth: HOUR_COL_W,
                      color: "var(--text-primary)",
                    }}
                  >
                    {moment().hour(h).minute(0).format("h A").replace(":00", "")}
                  </th>
                ))}
                {hasUnknownColumn && (
                  <th
                    style={{
                      textAlign: "center",
                      width: HOUR_COL_W,
                      minWidth: HOUR_COL_W,
                      color: "var(--text-primary)",
                    }}
                  >
                    Unknown
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={2 + hours.length} style={{ textAlign: "center", padding: 16 }}>
                    <Text className="v3-text-secondary">No schedules found for the selected range</Text>
                  </td>
                </tr>
              ) : (
                rows.map((row, idx) => {
                  const isFirstOfDate = idx === 0 || rows[idx - 1].dateISO !== row.dateISO;
                  const topBorder = isFirstOfDate ? "1px solid var(--border-light)" : undefined;
                  return (
                    <tr key={row.key}>
                      <td
                        style={{
                          whiteSpace: "nowrap",
                          position: "sticky",
                          left: LEFT_DATE,
                          background: "var(--bg-surface)",
                          zIndex: 2,
                          width: DATE_COL_W,
                          minWidth: DATE_COL_W,
                          maxWidth: DATE_COL_W,
                          borderTop: topBorder,
                          color: "var(--text-primary)",
                        }}
                      >
                        {row.dateLabel}
                      </td>
                      <td
                        style={{
                          position: "sticky",
                          left: LEFT_BRANCH,
                          background: "var(--bg-surface)",
                          zIndex: 2,
                          width: BRANCH_COL_W,
                          minWidth: BRANCH_COL_W,
                          maxWidth: BRANCH_COL_W,
                          borderTop: topBorder,
                          color: "var(--text-primary)",
                        }}
                      >
                        {row.branchName}
                      </td>
                      {(() => {
                        const tds = [];
                        let i = 0;
                        while (i < hours.length) {
                          const h = hours[i];
                          const slotsHere = row.hourMap[h] || [];
                          if (!slotsHere.length) {
                            tds.push(
                              <td
                                key={`${row.key}__${h}`}
                                style={{ width: HOUR_COL_W, minWidth: HOUR_COL_W, borderTop: topBorder }}
                              />
                            );
                            i += 1;
                            continue;
                          }

                          // Group slots by their time range to handle spanning
                          const slotGroups = [];
                          const processedSlots = new Set();

                          slotsHere.forEach((slot) => {
                            if (processedSlots.has(slot.timeFrame)) return;
                            processedSlots.add(slot.timeFrame);

                            // Find all hours this slot spans
                            const startHour = slot.startHour || h;
                            const endHour = slot.endHour || h;
                            const spanHours = [];

                            for (let hour = startHour; hour <= Math.min(endHour, 20); hour++) {
                              if (hours.includes(hour)) {
                                spanHours.push(hour);
                              }
                            }

                            slotGroups.push({
                              ...slot,
                              spanHours,
                              span: spanHours.length,
                              startHour: Math.min(...spanHours),
                              endHour: Math.max(...spanHours),
                            });
                          });

                          // Sort groups by start hour
                          slotGroups.sort((a, b) => a.startHour - b.startHour);

                          // Process each slot group
                          slotGroups.forEach((group) => {
                            const groupSlots = slotsHere.filter((s) => s.timeFrame === group.timeFrame);
                            const totalCapacity = groupSlots.reduce((sum, s) => sum + (s.capacity || 0), 0);
                            const totalBooked = groupSlots.reduce((sum, s) => sum + (s.bookedCount || 0), 0);
                            const firstClickable =
                              groupSlots.find((s) => s.isAvailable && !s.isFullyBooked) || groupSlots[0];

                            // Build tooltip with all required information

                            // 1. Slot timing information (TimeFrom - TimeTo) from schedules
                            const slotTimings = groupSlots
                              .map((s) => {
                                if (s.timeFrom && s.timeTo) {
                                  return `${s.timeFrom} - ${s.timeTo}`;
                                } else if (s.timeFrame) {
                                  return s.timeFrame;
                                }
                                return null;
                              })
                              .filter((t) => t)
                              .filter((t, idx, arr) => arr.indexOf(t) === idx) // Remove duplicate timings
                              .join(", ");

                            // 2. Surgery groups from schedules (DoctorScheduleMaster)
                            const surgeryGroups = groupSlots
                              .map((s) => s.surgeryGroups || [])
                              .flat()
                              .filter((g, idx, arr) => arr.indexOf(g) === idx) // Remove duplicates
                              .join(", ");

                            // 3. Scheduled times from surgeries (including duplicates as requested)
                            const scheduledTimes = groupSlots
                              .map((s) => s.schedule?.scheduledTime || s.timeFrom)
                              .filter((t) => t)
                              .join(", "); // Keep duplicates as requested

                            const tooltipParts = [
                              slotTimings ? `Slots: ${slotTimings}` : group.timeFrame,
                              surgeryGroups ? `Groups: ${surgeryGroups}` : "",
                              scheduledTimes ? `Scheduled: ${scheduledTimes}` : "",
                              `Capacity: ${totalBooked}/${totalCapacity}`,
                            ].filter(Boolean);

                            const clickable =
                              firstClickable && firstClickable.isAvailable && !firstClickable.isFullyBooked;
                            const bg =
                              totalBooked >= totalCapacity && totalCapacity > 0
                                ? "rgba(219, 55, 55, 0.18)"
                                : clickable
                                ? "rgba(19, 124, 189, 0.22)"
                                : "rgba(255, 255, 255, 0.10)";
                            const border =
                              totalBooked >= totalCapacity && totalCapacity > 0
                                ? "1px solid rgba(219,55,55,0.45)"
                                : clickable
                                ? "1px solid rgba(19,124,189,0.45)"
                                : "1px solid var(--border-light)";

                            tds.push(
                              <td
                                key={`${row.key}__${group.timeFrame}__${group.startHour}`}
                                colSpan={group.span}
                                style={{
                                  width: HOUR_COL_W * group.span,
                                  minWidth: HOUR_COL_W * group.span,
                                  padding: 1,
                                  borderTop: topBorder,
                                  verticalAlign: "middle",
                                }}
                              >
                                <div
                                  onClick={() =>
                                    clickable &&
                                    this.handleSlotClick(row.dateISO, row.branchName, firstClickable.timeFrame)
                                  }
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    padding: "4px 8px",
                                    borderRadius: 8,
                                    background: bg,
                                    border: border,
                                    cursor: clickable ? "pointer" : "default",
                                    height: 26,
                                    marginTop: -2,
                                    marginBottom: -2,
                                    textAlign: "center",
                                    width: "100%",
                                  }}
                                  title={tooltipParts.join("\n")}
                                >
                                  <Text
                                    style={{
                                      fontWeight: 800,
                                      color: "var(--text-primary)",
                                      margin: 0,
                                      fontSize: 14,
                                      lineHeight: 1,
                                    }}
                                  >
                                    {totalBooked}/{totalCapacity}
                                  </Text>
                                </div>
                              </td>
                            );
                          });

                          // Skip to the next unprocessed hour
                          const maxProcessedHour = Math.max(...slotGroups.map((g) => g.endHour));
                          i = hours.findIndex((hour) => hour > maxProcessedHour);
                          if (i === -1) break;
                        }
                        return tds;
                      })()}
                      {hasUnknownColumn && (
                        <td
                          style={{
                            width: HOUR_COL_W,
                            minWidth: HOUR_COL_W,
                            borderTop: topBorder,
                            textAlign: "center",
                          }}
                        >
                          {row.unknownCount ? (
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                padding: "4px 8px",
                                borderRadius: 8,
                                background: "rgba(219, 55, 55, 0.12)",
                                border: "1px dashed rgba(219,55,55,0.6)",
                                height: 26,
                                marginTop: -2,
                                marginBottom: -2,
                              }}
                              title="Bookings without a matching slot time"
                            >
                              <Text
                                style={{
                                  fontWeight: 700,
                                  color: "var(--text-primary)",
                                  margin: 0,
                                  fontSize: 13,
                                  lineHeight: 1,
                                }}
                              >
                                {row.unknownCount}
                              </Text>
                            </div>
                          ) : (
                            <span style={{ color: "var(--text-secondary)" }}>-</span>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </Card>
    );
  }

  render() {
    return (
      <div
        style={{
          padding: "20px",
          backgroundColor: "var(--bg-primary)",
          color: "var(--text-primary)",
          minHeight: "100vh",
        }}
      >
        {this.renderHeader()}
        {this.renderFilters()}
        {this.renderTable()}
      </div>
    );
  }
}

export default inject("authStore")(observer(withRouter(DoctorwiseCalendar)));
